<template>
  <div class="cc">
    <div class="cc-header">
      <div class="cc-nav">
        <template v-if="mode !== 'annee'">
          <button class="cc-nav-btn" :disabled="!canGoPrev" @click="goPrev">&#8249;</button>
          <div class="cc-period-wrap">
            <span class="cc-period-label cc-period-label--clickable" @click="openDatePicker">{{ periodLabel }}</span>
            <button class="cc-calendar-btn" @click="openDatePicker" title="Choisir dans le calendrier">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </button>
            <input
              v-if="mode !== 'mois'"
              ref="datePicker"
              type="date"
              class="cc-hidden-input"
              :min="dataMin"
              :max="dataMax"
              :value="currentDateValue"
              @change="onDatePickerChange"
            />
            <input
              v-else
              ref="datePicker"
              type="month"
              class="cc-hidden-input"
              :min="dataMinMonth"
              :max="dataMaxMonth"
              :value="currentDateValue"
              @change="onDatePickerChange"
            />
          </div>
          <button class="cc-nav-btn" :disabled="!canGoNext" @click="goNext">&#8250;</button>
        </template>
        <span v-else class="cc-period-label">{{ periodLabel }}</span>
      </div>
      <div class="cc-modes">
        <button
          v-for="m in modes"
          :key="m.key"
          class="cc-mode-btn"
          :class="{ 'cc-mode-btn--active': mode === m.key }"
          @click="setMode(m.key)"
        >{{ m.label }}</button>
      </div>
    </div>

    <div class="cc-canvas-wrap">
      <div v-if="loading" class="cc-loading">
        <div class="cc-spinner"></div>
      </div>
      <canvas ref="canvas"></canvas>
    </div>

    <div class="cc-unit">{{ unitLabel }}</div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const MONTH_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const MONTH_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
const JS_DAY_NAMES = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']

export default {
  name: 'PvChart',
  props: {
    pvgisStats: { type: Object, default: null },
    apiBaseEndpoint: { type: String, default: '/wizard/pvgis-profile' },
    extraParams: { type: Object, default: function() { return {} } },
    puissanceTotaleKwc: { type: Number, default: 0 },
    // Props legacy PVGIS (conservées pour compatibilité ascendante)
    lat: { type: Number, default: null },
    lon: { type: Number, default: null },
    tilt: { type: Number, default: 30 },
    aspect: { type: Number, default: 0 },
  },
  data() {
    return {
      mode: 'annee',
      selectedMonth: 0,
      selectedDayIdx: 0,
      weekStartIdx: 0,
      loading: false,
      chart: null,
    }
  },
  computed: {
    modes() {
      var all = [
        { key: 'annee', label: 'Année' },
        { key: 'mois', label: 'Mois' },
        { key: 'semaine', label: 'Semaine' },
        { key: 'jour', label: 'Jour' },
      ]
      // Sans endpoint pour les données horaires (ex. mode multi-toitures), masquer Jour/Semaine
      if (!this.apiBaseEndpoint) {
        return all.filter(function(m) { return m.key === 'annee' || m.key === 'mois' })
      }
      return all
    },
    dataYear() {
      return this.pvgisStats ? (this.pvgisStats.dataYear || 2020) : 2020
    },
    allAvailableDates() {
      if (!this.pvgisStats) return []
      var dates = []
      this.pvgisStats.dailyByMonth.forEach(function(days, monthIdx) {
        if (days && days.length) {
          days.forEach(function(d) { dates.push({ month: monthIdx + 1, day: d.day }) })
        }
      })
      return dates
    },
    availableMonths() {
      if (!this.pvgisStats) return []
      return this.pvgisStats.dailyByMonth
        .map(function(days, idx) { return idx })
        .filter(function(idx, i, arr) {
          var self = arr[i]
          // reference outer pvgisStats
          return true
        })
        .filter(function(idx) { return true }) // placeholder
    },
    currentDate() {
      if (!this.allAvailableDates.length) return null
      return this.allAvailableDates[Math.min(this.selectedDayIdx, this.allAvailableDates.length - 1)]
    },
    weekStartDate() {
      if (!this.allAvailableDates.length) return null
      return this.allAvailableDates[Math.min(this.weekStartIdx, this.allAvailableDates.length - 1)]
    },
    canGoPrev() {
      if (this.mode === 'mois') {
        var pos = this.computedAvailableMonths.indexOf(this.selectedMonth)
        return pos > 0
      }
      if (this.mode === 'jour') return this.selectedDayIdx > 0
      if (this.mode === 'semaine') return this.weekStartIdx >= 7
      return false
    },
    canGoNext() {
      if (this.mode === 'mois') {
        var pos = this.computedAvailableMonths.indexOf(this.selectedMonth)
        return pos < this.computedAvailableMonths.length - 1
      }
      if (this.mode === 'jour') return this.selectedDayIdx < this.allAvailableDates.length - 1
      if (this.mode === 'semaine') return this.weekStartIdx + 7 < this.allAvailableDates.length
      return false
    },
    computedAvailableMonths() {
      if (!this.pvgisStats) return []
      var result = []
      this.pvgisStats.dailyByMonth.forEach(function(days, idx) {
        if (days && days.length > 0) result.push(idx)
      })
      return result
    },
    periodLabel() {
      // année typique (jours de 2021, 2022 et 2023 mêlés) : ni année ni jour de la semaine affichés (12/10/2026)
      if (this.mode === 'annee') return 'Production sur une année typique'
      if (this.mode === 'mois') return MONTH_NAMES[this.selectedMonth] + ' — année typique'
      if (this.mode === 'jour') {
        if (!this.currentDate) return '—'
        return this.currentDate.day + ' ' + MONTH_SHORT[this.currentDate.month - 1] + ' — année typique'
      }
      if (this.mode === 'semaine') {
        if (!this.weekStartDate) return '—'
        var ws = this.weekStartDate
        var d2 = new Date(this.dataYear, ws.month - 1, ws.day + 6)
        return ws.day + ' ' + MONTH_SHORT[ws.month - 1] + ' – ' + d2.getDate() + ' ' + MONTH_SHORT[d2.getMonth()] + ' — année typique'
      }
      return ''
    },
    isScaled() {
      return this.puissanceTotaleKwc > 0
    },
    unitLabel() {
      if (this.mode === 'jour' || this.mode === 'semaine') {
        return this.isScaled
          ? 'Puissance produite (kW) — installation ' + this.puissanceTotaleKwc.toFixed(2) + ' kWc'
          : 'Puissance (kW/kWc)'
      }
      return this.isScaled
        ? 'Production (kWh) — installation ' + this.puissanceTotaleKwc.toFixed(2) + ' kWc'
        : 'Production (kWh/kWc)'
    },
    dataMin() {
      if (!this.allAvailableDates.length) return ''
      var first = this.allAvailableDates[0]
      return this.dataYear + '-' + String(first.month).padStart(2, '0') + '-' + String(first.day).padStart(2, '0')
    },
    dataMax() {
      if (!this.allAvailableDates.length) return ''
      var last = this.allAvailableDates[this.allAvailableDates.length - 1]
      return this.dataYear + '-' + String(last.month).padStart(2, '0') + '-' + String(last.day).padStart(2, '0')
    },
    dataMinMonth() {
      if (!this.allAvailableDates.length) return ''
      var first = this.allAvailableDates[0]
      return this.dataYear + '-' + String(first.month).padStart(2, '0')
    },
    dataMaxMonth() {
      if (!this.allAvailableDates.length) return ''
      var last = this.allAvailableDates[this.allAvailableDates.length - 1]
      return this.dataYear + '-' + String(last.month).padStart(2, '0')
    },
    currentDateValue() {
      if (this.mode === 'mois') {
        return this.dataYear + '-' + String(this.selectedMonth + 1).padStart(2, '0')
      }
      if (this.mode === 'jour' && this.currentDate) {
        var cd = this.currentDate
        return this.dataYear + '-' + String(cd.month).padStart(2, '0') + '-' + String(cd.day).padStart(2, '0')
      }
      if (this.mode === 'semaine' && this.weekStartDate) {
        var ws = this.weekStartDate
        return this.dataYear + '-' + String(ws.month).padStart(2, '0') + '-' + String(ws.day).padStart(2, '0')
      }
      return ''
    },
  },
  watch: {
    pvgisStats: function(val) {
      if (val) this.initFromStats()
    },
  },
  mounted: function() {
    if (this.pvgisStats) this.initFromStats()
  },
  beforeDestroy: function() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
  },
  methods: {
    openDatePicker: function() {
      var input = this.$refs.datePicker
      if (!input) return
      if (input.showPicker) {
        input.showPicker()
      } else {
        input.click()
      }
    },
    onDatePickerChange: function(event) {
      var value = event.target.value
      if (!value) return
      if (this.mode === 'mois') {
        var parts = value.split('-')
        var monthIdx = parseInt(parts[1], 10) - 1
        if (this.computedAvailableMonths.indexOf(monthIdx) >= 0) {
          this.selectedMonth = monthIdx
          this.refreshChart()
        }
      } else {
        var pts = value.split('-')
        var month = parseInt(pts[1], 10)
        var day = parseInt(pts[2], 10)
        var self = this
        var idx = this.allAvailableDates.findIndex(function(d) { return d.month === month && d.day === day })
        var targetIdx = idx >= 0 ? idx : this._nearestDateIdx(month, day)
        if (this.mode === 'jour') {
          this.selectedDayIdx = targetIdx
        } else if (this.mode === 'semaine') {
          this.weekStartIdx = Math.min(
            Math.floor(targetIdx / 7) * 7,
            Math.max(0, this.allAvailableDates.length - 1)
          )
        }
        this.refreshChart()
      }
    },
    _nearestDateIdx: function(month, day) {
      var best = 0
      var bestDiff = Infinity
      this.allAvailableDates.forEach(function(d, i) {
        var diff = Math.abs((d.month - month) * 31 + (d.day - day))
        if (diff < bestDiff) { bestDiff = diff; best = i }
      })
      return best
    },
    initFromStats: function() {
      this.selectedMonth = this.computedAvailableMonths.length > 0 ? this.computedAvailableMonths[0] : 0
      this.selectedDayIdx = 0
      this.weekStartIdx = 0
      this.refreshChart()
    },
    setMode: function(m) {
      this.mode = m
      this.refreshChart()
    },
    goPrev: function() {
      if (this.mode === 'mois') {
        var pos = this.computedAvailableMonths.indexOf(this.selectedMonth)
        if (pos > 0) this.selectedMonth = this.computedAvailableMonths[pos - 1]
      } else if (this.mode === 'jour') {
        if (this.selectedDayIdx > 0) this.selectedDayIdx--
      } else if (this.mode === 'semaine') {
        this.weekStartIdx = Math.max(0, this.weekStartIdx - 7)
      }
      this.refreshChart()
    },
    goNext: function() {
      if (this.mode === 'mois') {
        var pos = this.computedAvailableMonths.indexOf(this.selectedMonth)
        if (pos < this.computedAvailableMonths.length - 1) this.selectedMonth = this.computedAvailableMonths[pos + 1]
      } else if (this.mode === 'jour') {
        if (this.selectedDayIdx < this.allAvailableDates.length - 1) this.selectedDayIdx++
      } else if (this.mode === 'semaine') {
        this.weekStartIdx = Math.min(this.weekStartIdx + 7, this.allAvailableDates.length - 1)
      }
      this.refreshChart()
    },
    refreshChart: function() {
      if (this.mode === 'annee') {
        this.renderAnnee()
      } else if (this.mode === 'mois') {
        this.renderMois()
      } else if (this.mode === 'semaine') {
        this.fetchAndRenderSemaine()
      } else if (this.mode === 'jour') {
        this.fetchAndRenderJour()
      }
    },
    renderAnnee: function() {
      if (!this.pvgisStats) return
      var scale = this.isScaled ? this.puissanceTotaleKwc : 1
      var labels = this.pvgisStats.monthly.map(function(m, i) { return MONTH_ABBR[i] })
      var data = this.pvgisStats.monthly.map(function(m) { return parseFloat((m.kwh * scale).toFixed(1)) })
      this.buildChart('bar', labels, data)
    },
    renderMois: function() {
      if (!this.pvgisStats) return
      var scale = this.isScaled ? this.puissanceTotaleKwc : 1
      var days = this.pvgisStats.dailyByMonth[this.selectedMonth] || []
      var labels = days.map(function(d) { return String(d.day) })
      var data = days.map(function(d) { return parseFloat((d.kwh * scale).toFixed(2)) })
      this.buildChart('bar', labels, data)
    },
    fetchAndRenderSemaine: function() {
      if (!this.weekStartDate || !this.apiBaseEndpoint) return
      this.loading = true
      var self = this
      var ws = this.weekStartDate
      var token = authenticationService.status.jwtToken
      var params = Object.assign({}, self.extraParams, { month: ws.month, day: ws.day })
      // Fallback legacy props si extraParams vide
      if (!Object.keys(self.extraParams).length && self.lat) {
        params = { lat: self.lat, lon: self.lon, tilt: self.tilt, aspect: self.aspect, month: ws.month, day: ws.day }
      }
      var scale = self.isScaled ? self.puissanceTotaleKwc : 1
      axios.get(conf.API_URL + self.apiBaseEndpoint + '/week-data', {
        params: params,
        headers: { Authorization: 'Bearer ' + token },
      }).then(function(resp) {
        var pts = (resp.data && resp.data.data) ? resp.data.data : []
        var labels = pts.map(function(p) { return p.label })
        var data = pts.map(function(p) { return parseFloat((p.kw * scale).toFixed(3)) })
        self.buildChart('line', labels, data)
      }).catch(function() {}).then(function() { self.loading = false })
    },
    fetchAndRenderJour: function() {
      if (!this.currentDate || !this.apiBaseEndpoint) return
      this.loading = true
      var self = this
      var cd = this.currentDate
      var token = authenticationService.status.jwtToken
      var params = Object.assign({}, self.extraParams, { month: cd.month, day: cd.day })
      // Fallback legacy props si extraParams vide
      if (!Object.keys(self.extraParams).length && self.lat) {
        params = { lat: self.lat, lon: self.lon, tilt: self.tilt, aspect: self.aspect, month: cd.month, day: cd.day }
      }
      var scale = self.isScaled ? self.puissanceTotaleKwc : 1
      axios.get(conf.API_URL + self.apiBaseEndpoint + '/day-data', {
        params: params,
        headers: { Authorization: 'Bearer ' + token },
      }).then(function(resp) {
        var pts = (resp.data && resp.data.data) ? resp.data.data : []
        var labels = pts.map(function(p) { return p.time })
        var data = pts.map(function(p) { return parseFloat((p.watts / 1000 * scale).toFixed(3)) })
        self.buildChart('line', labels, data)
      }).catch(function() {}).then(function() { self.loading = false })
    },
    buildChart: function(type, labels, data) {
      if (this.chart) { this.chart.destroy(); this.chart = null }
      var canvas = this.$refs.canvas
      if (!canvas) return

      var isBar = type === 'bar'
      var isSemaine = this.mode === 'semaine'
      var color = '#e67e00'
      var scaled = this.isScaled

      var ticksConfig = {
        fontSize: 11,
        fontColor: '#aaa',
        maxRotation: 0,
        autoSkip: !isSemaine,
        maxTicksLimit: isSemaine ? undefined : (this.mode === 'jour' ? 9 : 12),
      }
      if (isSemaine) {
        ticksConfig.callback = function(value, index) { return index % 24 === 0 ? value : '' }
      }

      this.chart = new Chart(canvas, {
        type: type,
        data: {
          labels: labels,
          datasets: [{
            label: 'Production',
            data: data,
            backgroundColor: isBar ? 'rgba(230, 126, 0, 0.75)' : 'rgba(230, 126, 0, 0.08)',
            borderColor: color,
            borderWidth: isBar ? 0 : 1.5,
            pointRadius: 0,
            fill: !isBar,
            lineTension: 0.2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(item) {
                var val = parseFloat(item.yLabel).toFixed(2)
                var unit = isBar
                  ? (scaled ? ' kWh' : ' kWh/kWc')
                  : (scaled ? ' kW' : ' kW/kWc')
                return ' ' + val + unit
              },
            },
            backgroundColor: 'rgba(60, 40, 0, 0.88)',
            titleFontColor: '#fff',
            bodyFontColor: '#fff',
            borderColor: '#e67e00',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
          },
          scales: {
            xAxes: [{
              gridLines: { display: false },
              ticks: ticksConfig,
            }],
            yAxes: [{
              gridLines: { color: '#ececec', drawBorder: false, zeroLineColor: '#ddd' },
              ticks: {
                fontSize: 11,
                fontColor: '#aaa',
                beginAtZero: true,
                callback: function(v) {
                  if (v === 0) return '0'
                  return v
                },
              },
            }],
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.cc {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  padding: 16px 20px 12px;
}

.cc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.cc-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cc-nav-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.cc-nav-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #aaa;
}

.cc-nav-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.cc-period-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.cc-period-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  min-width: 160px;
  text-align: center;
}

.cc-period-label--clickable {
  cursor: pointer;
  text-decoration: underline dotted #aaa;
  text-underline-offset: 3px;
}

.cc-period-label--clickable:hover {
  color: #e67e00;
}

.cc-calendar-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.cc-calendar-btn:hover {
  background: #fff8ee;
  color: #e67e00;
  border-color: #e67e00;
}

.cc-hidden-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
  top: 100%;
  left: 50%;
}

.cc-modes {
  display: flex;
  gap: 4px;
}

.cc-mode-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #666;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}

.cc-mode-btn:hover {
  background: #f5f5f5;
}

.cc-mode-btn--active {
  background: #e67e00;
  color: #fff;
  border-color: #e67e00;
  font-weight: 600;
}

.cc-canvas-wrap {
  position: relative;
  height: 280px;
}

.cc-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.7);
  z-index: 2;
}

.cc-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #eee;
  border-top-color: #e67e00;
  border-radius: 50%;
  animation: cc-spin 0.7s linear infinite;
}

@keyframes cc-spin { to { transform: rotate(360deg); } }

.cc-unit {
  font-size: 11px;
  color: #aaa;
  text-align: center;
  margin-top: 6px;
}
</style>
